var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { Portal } from "../_Portal";
import { calculatePosition } from "./utils/calculatePosition";
// styles
import "./floatingContent.scss";
// use the optional prop interface to define the default props
const defaultProps = {
    "data-testid": "tecma-floatingContent",
    position: "bottom-left",
    action: "click",
    parentElement: undefined,
    arrow: false,
    clickOutsideToToggle: true,
    offset: 0,
};
const FloatingContent = (_a) => {
    var { trigger, children, className, onToggle, action, isShown, position, triggerClassName, clickOutsideToToggle, parentElement, arrow, offset, fixed, id, style } = _a, rest = __rest(_a, ["trigger", "children", "className", "onToggle", "action", "isShown", "position", "triggerClassName", "clickOutsideToToggle", "parentElement", "arrow", "offset", "fixed", "id", "style"]);
    const classList = classNames("tecma-floatingContent-container", { [`${position}`]: position }, className);
    const triggerClassList = classNames("floatingContent-trigger", triggerClassName);
    const triggerRef = useRef(null);
    const floatingContentRef = useRef(null);
    const [nextStyle, setNextStyle] = useState();
    const [mouseIsHovering, setMouseHover] = useState(false);
    useEffect(() => {
        if (triggerRef.current && position && floatingContentRef.current && isShown) {
            const triggerPosition = triggerRef.current.getBoundingClientRect();
            const triggerParentValues = parentElement === null || parentElement === void 0 ? void 0 : parentElement.getBoundingClientRect();
            const floatingContentPosition = floatingContentRef.current.getBoundingClientRect();
            setNextStyle(calculatePosition(triggerPosition, floatingContentPosition, position, triggerParentValues, arrow, offset, fixed));
            /**
             * focusing element is required to handle the onBlur event on the floatingContent container
             * this way, clicking anywhere outside of it, the floatingContent will be closed
             */
            floatingContentRef.current.focus({ preventScroll: true });
        }
    }, [isShown, position]);
    // handles the clicks outside the floating content (and outside the current trigger)
    const clickOutsideHandler = ({ target }) => {
        if (isShown &&
            triggerRef.current &&
            !triggerRef.current.contains(target) &&
            floatingContentRef.current &&
            !floatingContentRef.current.contains(target)) {
            onToggle();
        }
    };
    // adds the click event listener when clickOutsideToToggle is true and when action is click
    useEffect(() => {
        if (triggerRef.current &&
            floatingContentRef.current &&
            clickOutsideToToggle &&
            action === "click") {
            document.addEventListener("click", clickOutsideHandler);
        }
        return () => {
            document.removeEventListener("click", clickOutsideHandler);
        };
    }, [triggerRef.current, floatingContentRef.current]);
    /**
     * onMouseEnter and onMouseLeave functions are both used to handle the hover action by toggling the
     * mouseIsHovering state and firing the onToggle callback
     */
    const onMouseEnter = () => {
        if (!mouseIsHovering) {
            setMouseHover(true);
            onToggle();
        }
    };
    const onMouseLeave = () => {
        if (mouseIsHovering) {
            setMouseHover(false);
            onToggle();
        }
    };
    const actions = {
        onClick: action === "click" ? onToggle : undefined,
        onMouseEnter: action === "hover" ? onMouseEnter : undefined,
        onMouseLeave: action === "hover" ? onMouseLeave : undefined,
        /**
         * When scrolling, onMouseLeave event is not triggered, so the floatingContent won't be toggled.
         * To handle this situation, onWheel is the right event to fire
         */
        onWheel: action === "hover" ? onMouseLeave : undefined,
    };
    return (_jsxs(_Fragment, { children: [_jsx("span", Object.assign({ ref: triggerRef, className: triggerClassList }, rest, actions, { children: trigger })), isShown && (_jsx(Portal, { id: "tecma-floatingContent", className: fixed ? "fixed" : "", children: _jsxs("div", { className: classList, style: Object.assign(Object.assign({}, style), nextStyle === null || nextStyle === void 0 ? void 0 : nextStyle.floatingContentStyle), id: id, ref: floatingContentRef, children: [arrow && (_jsx("div", { className: `floatingContent-arrow ${className || ""}`, style: Object.assign({}, nextStyle === null || nextStyle === void 0 ? void 0 : nextStyle.arrowStyle) })), children] }) }))] }));
};
FloatingContent.defaultProps = defaultProps;
export default React.memo(FloatingContent);
